import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBlocker, useNavigate, useParams } from "react-router";
import { toast, Bounce } from "react-toastify";
import {
    fetchPlanManagerClientConfig,
    fetchPlanManagerClientConfigByName,
    fetchPlanManagerCreateClientConfigV2,
    fetchPlanManagerHealthEdgeConfig,
    fetchPlanManagerHealthEdgePlansBatch,
    fetchPlanManagerNetworkSeriesOptions,
    fetchPlanManagerPlansBatch,
    fetchPlanManagerPublishOeConfig,
    fetchPlanManagerUnpublishOeConfig,
    fetchPlanManagerUpdateClientConfigV2,
    fetchPlanManagerValidateOeUrl,
} from "../../../../../utils/apiUtils.js";
import {
    NetworkDirectoryType,
    appendEmptyNetworkConfig,
    buildOeClientConfigCreateBody,
    buildOeClientConfigUpdateBody,
    buildOeUpdatePlansLookup,
    buildPlanAffiliationClientOptions,
    createEmptyConfigFormState,
    hasUnaffiliatedPlans,
    mapClientConfigResponseToFormState,
    mapNetworkSeriesOptionsResponse,
    mapValidateOeUrlResponse,
    parseNetworkNameOptions,
    removeNetworkConfigAtIndex,
    resolveClientConfigSaveEnabled,
    resolveExcludesAllPreEnrollmentSites,
    resolvePlanAffiliationTabEnabled,
    resolvePlanAffiliationTabDisabledTooltip,
    resolvePreEnrollmentTabEnabled,
    resolvePreEnrollmentTabDisabledTooltip,
    validateSkipUrlCheckOnEvent,
} from "../../../../../utils/planManager/networkConfigurationUtils.js";
import { loadAllPlansGrids } from "../../../../../utils/planManager/planConfigurationUtils.js";
import { ConfigTabId, NETWORKS_LIST_ROUTE, PLAN_MANAGER_LONG_FETCH_QUERY_OPTIONS, PLAN_MANAGER_QUERY_KEYS } from "../../../common/constants";


/**
 * Toast messages for Networks Config save and publish actions.
 * @readonly
 */
export const CONFIG_TOAST_MESSAGES = Object.freeze({
    SAVE: "Client configuration saved successfully!",
    SAVE_ERROR: "Failed to save the client configuration.",
    PUBLISH: "Pre-enrollment site published!",
    PUBLISH_ERROR: "Failed to publish the pre-enrollment site.",
    UNPUBLISH: "Pre-enrollment site unpublished!",
    UNPUBLISH_ERROR: "Failed to unpublish the pre-enrollment site.",
    WIPE_AFFILIATION: "Plan affiliations have been wiped!",
});

/**
 * Shared `react-toastify` options for stub action toasts on this screen.
 * @type {import("react-toastify").ToastOptions}
 */
const CONFIG_TOAST_OPTIONS = Object.freeze({
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
    transition: Bounce,
});

const CONFIG_LOAD_ERROR_MESSAGE =
    "Plan manager data is currently unavailable. Please try again later.";

const CLOSED_MODALS = Object.freeze({
    excludePreEnrollment: false,
    saveWithoutPublish: false,
    planAffiliationWarning: false,
    unsavedWork: false,
    deleteNetwork: false,
    confirmEditUrl: false,
    publish: false,
    unpublish: false,
    removeCustomContent: false,
});

/**
 * Locks pre-enrollment edits while the site is published. An unpublished site
 * stays editable; a saved URL still starts disabled until the edit control is
 * used.
 * @param {object} preEnrollment
 * @returns {object}
 */
const lockSavedCustomContent = (preEnrollment) => {
    const hasSavedUrl = Boolean((preEnrollment?.siteURL ?? "").trim());
    const publish = Boolean(preEnrollment?.publish);

    return {
        ...preEnrollment,
        publish,
        isOeUrlDisabled: hasSavedUrl,
        isCustomContentDisabled: publish,
    };
};

const EMPTY_FORM_STATE = {
    clientName: "",
    networkConfigs: [],
    preEnrollment: {
        customContent: false,
        siteURL: "",
        publish: false,
        displayPcpActivationBadge: true,
        displayReferralRequiredBadge: true,
        isOeUrlDisabled: false,
        isCustomContentDisabled: false,
        isUrlValid: false,
        isUrlUnique: null,
        shouldShowUrlCheck: false,
    },
    planAffiliation: {
        planType: "",
        selectedClientId: "",
        plans: [],
    },
};

/**
 * Ephemeral state and handlers for the client network configuration screen.
 * @returns {object} Values and callbacks for the presentational view.
 */
export function useConfig() {
    const { clientName: clientNameParam } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const nextRowIdRef = useRef(1);
    const hydrationKeyRef = useRef(null);

    const [clientName, setClientName] = useState(EMPTY_FORM_STATE.clientName);
    const [networkConfigs, setNetworkConfigs] = useState(
        EMPTY_FORM_STATE.networkConfigs,
    );
    const [preEnrollment, setPreEnrollment] = useState(
        EMPTY_FORM_STATE.preEnrollment,
    );
    const [planAffiliation, setPlanAffiliation] = useState(
        EMPTY_FORM_STATE.planAffiliation,
    );
    const [selectedTabId, setSelectedTabId] = useState(ConfigTabId.NETWORKS);
    const [modals, setModals] = useState(CLOSED_MODALS);
    const [deleteNetworkIndex, setDeleteNetworkIndex] = useState(null);
    const [savedSuccess, setSavedSuccess] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const hasUnsavedChangesRef = useRef(false);
    const setUnsavedChanges = (isUnsaved) => {
        hasUnsavedChangesRef.current = isUnsaved;
        setHasUnsavedChanges(isUnsaved);
    };
    const navigationBlocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            hasUnsavedChangesRef.current &&
            currentLocation.pathname !== nextLocation.pathname,
    );
    const [isFormHydrated, setIsFormHydrated] = useState(false);

    const networkSeriesQuery = useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.networkSeriesOptions,
        queryFn: fetchPlanManagerNetworkSeriesOptions,
    });

    const clientConfigQuery = useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.clientConfig(clientNameParam),
        queryFn: () => fetchPlanManagerClientConfigByName(clientNameParam),
        enabled: Boolean(clientNameParam),
    });

    const plansAllQuery = useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.plansAll,
        queryFn: () =>
            loadAllPlansGrids({
                fetchJavelinaConfigPage: fetchPlanManagerClientConfig,
                fetchJavelinaBatch: fetchPlanManagerPlansBatch,
                fetchHealthEdgeConfigPage: fetchPlanManagerHealthEdgeConfig,
                fetchHealthEdgeBatch: fetchPlanManagerHealthEdgePlansBatch,
            }),
        ...PLAN_MANAGER_LONG_FETCH_QUERY_OPTIONS,
    });

    const seriesOptions = useMemo(
        () => mapNetworkSeriesOptionsResponse(networkSeriesQuery.data ?? []),
        [networkSeriesQuery.data],
    );

    const seriesKeys = useMemo(() => Object.keys(seriesOptions), [seriesOptions]);
    const seriesCount = seriesKeys.length;

    useEffect(() => {
        hydrationKeyRef.current = null;
        setIsFormHydrated(false);
    }, [clientNameParam]);

    useEffect(() => {
        if (networkSeriesQuery.isLoading || networkSeriesQuery.isError) {
            return;
        }

        if (clientNameParam && clientConfigQuery.isLoading) {
            return;
        }

        if (clientNameParam && clientConfigQuery.isError) {
            return;
        }

        const hydrationKey = clientNameParam
            ? `edit:${clientNameParam}`
            : "create";

        if (hydrationKeyRef.current === hydrationKey) {
            return;
        }

        const formState = clientNameParam
            ? mapClientConfigResponseToFormState(
                  clientConfigQuery.data ?? {},
                  seriesKeys,
              )
            : createEmptyConfigFormState(seriesKeys);

        setClientName(formState.clientName);
        setNetworkConfigs(formState.networkConfigs);
        setPreEnrollment(formState.preEnrollment);
        setPlanAffiliation(formState.planAffiliation);
        setUnsavedChanges(false);
        setSavedSuccess(false);
        hydrationKeyRef.current = hydrationKey;
        setIsFormHydrated(true);
    }, [
        clientNameParam,
        clientConfigQuery.data,
        clientConfigQuery.isError,
        clientConfigQuery.isLoading,
        networkSeriesQuery.isError,
        networkSeriesQuery.isLoading,
        seriesKeys,
    ]);

    const clientOptions = useMemo(
        () =>
            buildPlanAffiliationClientOptions({
                jvPlans: plansAllQuery.data?.jvPlans ?? [],
                hrpPlans: plansAllQuery.data?.hrpPlans ?? [],
                planType: planAffiliation.planType,
                currentClientName: clientNameParam || clientName,
            }),
        [
            plansAllQuery.data,
            planAffiliation.planType,
            clientNameParam,
            clientName,
        ],
    );

    const isPreEnrollmentEnabled = resolvePreEnrollmentTabEnabled(networkConfigs);
    const preEnrollmentDisabledTooltip =
        resolvePreEnrollmentTabDisabledTooltip(networkConfigs);
    const isPlanAffiliationEnabled =
        resolvePlanAffiliationTabEnabled(networkConfigs);
    const planAffiliationDisabledTooltip =
        resolvePlanAffiliationTabDisabledTooltip(networkConfigs);
    const isSaveEnabled = resolveClientConfigSaveEnabled({
        clientName,
        networkConfigs,
    });
    const networkNameOptions = useMemo(
        () => parseNetworkNameOptions(networkConfigs),
        [networkConfigs],
    );

    const isLoading =
        networkSeriesQuery.isLoading ||
        (Boolean(clientNameParam) && clientConfigQuery.isLoading) ||
        !isFormHydrated;
    const isError =
        networkSeriesQuery.isError ||
        (Boolean(clientNameParam) && clientConfigQuery.isError);
    const loadErrorMessage =
        networkSeriesQuery.error?.message ??
        clientConfigQuery.error?.message ??
        CONFIG_LOAD_ERROR_MESSAGE;

    const openModal = (name) =>
        setModals((previous) => ({ ...previous, [name]: true }));
    const closeModal = (name) =>
        setModals((previous) => ({ ...previous, [name]: false }));

    const markDirty = () => {
        setSavedSuccess(false);
        setUnsavedChanges(true);
    };

    useEffect(() => {
        if (navigationBlocker.state !== "blocked") {
            return;
        }

        setModals((previous) =>
            previous.unsavedWork
                ? previous
                : { ...previous, unsavedWork: true },
        );
    }, [navigationBlocker.state]);

    useEffect(() => {
        if (!hasUnsavedChanges) {
            return undefined;
        }

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const patchNetworkAtIndex = (index, patch) => {
        setNetworkConfigs((previous) =>
            previous.map((config, rowIndex) =>
                rowIndex === index ? { ...config, ...patch } : config,
            ),
        );
        markDirty();
    };

    const handleClientNameChange = (value) => {
        setClientName(value);
        markDirty();
    };

    const handleNetworkFieldChange = (index, field, value) =>
        patchNetworkAtIndex(index, { [field]: value });

    const handleDirectoryTypeChange = (index, directoryType) => {
        const patch = { directoryType };
        if (directoryType === NetworkDirectoryType.EXTERNAL) {
            patch.includePreEnrollmentSite = false;
        }
        patchNetworkAtIndex(index, patch);
    };

    const handleIncludePreEnrollmentChange = (index, include) =>
        patchNetworkAtIndex(index, { includePreEnrollmentSite: include });

    const handleSeriesChange = (index, seriesIndex, value) => {
        setNetworkConfigs((previous) =>
            previous.map((config, rowIndex) => {
                if (rowIndex !== index) {
                    return config;
                }
                const networkSeries = [...config.networkSeries];
                networkSeries[seriesIndex] = value;

                if (seriesIndex === 0 && value && !networkSeries[1]) {
                    networkSeries[1] = "601";
                }

                let preferredNetworkCodes = config.preferredNetworkCodes;
                const preferredSeriesIndex = seriesKeys.indexOf("900 series");
                if (
                    seriesIndex === preferredSeriesIndex &&
                    Array.isArray(preferredNetworkCodes)
                ) {
                    const allowedCodes = Array.isArray(value)
                        ? value
                        : value
                          ? [String(value)]
                          : [];
                    preferredNetworkCodes = preferredNetworkCodes.filter(
                        (code) => allowedCodes.includes(code),
                    );
                }

                return { ...config, networkSeries, preferredNetworkCodes };
            }),
        );
        markDirty();
    };

    const handlePreferredChange = (index, codes) => {
        const nextCodes = Array.isArray(codes)
            ? [...codes]
            : codes
              ? [codes]
              : [];
        patchNetworkAtIndex(index, {
            preferredNetworkCodes: nextCodes,
        });
    };

    const handleExternalFieldChange = (index, field, value) =>
        patchNetworkAtIndex(index, { [field]: value });

    const handleAddNetwork = () => {
        nextRowIdRef.current += 1;
        setNetworkConfigs((previous) =>
            appendEmptyNetworkConfig(
                previous,
                `row-added-${nextRowIdRef.current}`,
                seriesCount,
            ),
        );
        markDirty();
    };

    const removeNetworkAtIndex = (index) => {
        const targetRowId = networkConfigs[index]?.rowId;
        nextRowIdRef.current += 1;
        setNetworkConfigs((previous) =>
            removeNetworkConfigAtIndex(
                previous,
                index,
                `row-added-${nextRowIdRef.current}`,
                seriesCount,
            ),
        );
        setPlanAffiliation((previous) => ({
            ...previous,
            plans: previous.plans.map((plan) =>
                plan.networkAffiliation === targetRowId
                    ? { ...plan, networkAffiliation: "" }
                    : plan,
            ),
        }));
        markDirty();
    };

    const handleDeleteClick = (index) => {
        const targetRowId = networkConfigs[index]?.rowId;
        const isAffiliated = planAffiliation.plans.some(
            (plan) => plan.networkAffiliation === targetRowId,
        );

        if (isAffiliated) {
            setDeleteNetworkIndex(index);
            openModal("deleteNetwork");
            return;
        }

        removeNetworkAtIndex(index);
    };

    const handleDeleteNetworkConfirm = () => {
        if (deleteNetworkIndex !== null) {
            removeNetworkAtIndex(deleteNetworkIndex);
            toast.info(
                CONFIG_TOAST_MESSAGES.WIPE_AFFILIATION,
                CONFIG_TOAST_OPTIONS,
            );
        }
        setDeleteNetworkIndex(null);
        closeModal("deleteNetwork");
    };

    const handleTabChange = (tabId) => setSelectedTabId(tabId);

    const invalidateClientQueries = async (targetName) => {
        await queryClient.invalidateQueries({
            queryKey: ["planManager", "clientConfigs"],
        });
        const nameToInvalidate = targetName || clientNameParam || clientName;
        if (nameToInvalidate) {
            await queryClient.invalidateQueries({
                queryKey: PLAN_MANAGER_QUERY_KEYS.clientConfig(nameToInvalidate),
            });
        }
    };

    const commitSave = async () => {
        if (clientNameParam) {
            const plansLookup = buildOeUpdatePlansLookup(
                {
                    ...(plansAllQuery.data ?? {}),
                    planType: planAffiliation.planType,
                },
                clientConfigQuery.data?.planAffiliations?.plans,
            );
            const body = buildOeClientConfigUpdateBody(
                {
                    clientName,
                    networkConfigs,
                    preEnrollment,
                    planAffiliation,
                },
                { plansLookup },
            );

            try {
                const result = await fetchPlanManagerUpdateClientConfigV2(
                    clientNameParam,
                    body,
                );
                if (result == null) {
                    toast.error(
                        CONFIG_TOAST_MESSAGES.SAVE_ERROR,
                        CONFIG_TOAST_OPTIONS,
                    );
                    return;
                }

                await invalidateClientQueries(clientNameParam);
                toast.success(CONFIG_TOAST_MESSAGES.SAVE, CONFIG_TOAST_OPTIONS);
                setPreEnrollment(lockSavedCustomContent);
                setSavedSuccess(true);
                setUnsavedChanges(false);
                setModals(CLOSED_MODALS);
            } catch {
                toast.error(
                    CONFIG_TOAST_MESSAGES.SAVE_ERROR,
                    CONFIG_TOAST_OPTIONS,
                );
            }
            return;
        }

        const body = buildOeClientConfigCreateBody({
            clientName,
            networkConfigs,
            preEnrollment,
            planAffiliation,
        });

        try {
            const result = await fetchPlanManagerCreateClientConfigV2(body);
            if (result == null) {
                toast.error(
                    CONFIG_TOAST_MESSAGES.SAVE_ERROR,
                    CONFIG_TOAST_OPTIONS,
                );
                return;
            }

            await invalidateClientQueries(clientName);
            toast.success(CONFIG_TOAST_MESSAGES.SAVE, CONFIG_TOAST_OPTIONS);
            setPreEnrollment(lockSavedCustomContent);
            setSavedSuccess(true);
            setUnsavedChanges(false);
            setModals(CLOSED_MODALS);
        } catch {
            toast.error(CONFIG_TOAST_MESSAGES.SAVE_ERROR, CONFIG_TOAST_OPTIONS);
        }
    };

    const handleSave = () => {
        if (resolveExcludesAllPreEnrollmentSites(networkConfigs)) {
            openModal("excludePreEnrollment");
            return;
        }
        if (isPreEnrollmentEnabled && !preEnrollment.publish) {
            openModal("saveWithoutPublish");
            return;
        }
        if (hasUnaffiliatedPlans(planAffiliation.plans)) {
            openModal("planAffiliationWarning");
            return;
        }
        void commitSave();
    };

    const handleCustomContentChange = (customContent) => {
        if (preEnrollment.publish || preEnrollment.isCustomContentDisabled) {
            return;
        }
        if (!customContent && preEnrollment.customContent) {
            openModal("removeCustomContent");
            return;
        }
        setPreEnrollment((previous) => ({ ...previous, customContent }));
        markDirty();
    };

    const handleDisplayPcpActivationBadgeChange = (displayPcpActivationBadge) => {
        if (preEnrollment.publish) {
            return;
        }
        setPreEnrollment((previous) => ({
            ...previous,
            displayPcpActivationBadge,
        }));
        markDirty();
    };

    const handleDisplayReferralRequiredBadgeChange = (
        displayReferralRequiredBadge,
    ) => {
        if (preEnrollment.publish) {
            return;
        }
        setPreEnrollment((previous) => ({
            ...previous,
            displayReferralRequiredBadge,
        }));
        markDirty();
    };

    const handleSiteUrlChange = (siteURL) => {
        if (preEnrollment.publish) {
            return;
        }
        setPreEnrollment((previous) => ({
            ...previous,
            siteURL,
            isUrlUnique: null,
            shouldShowUrlCheck: false,
        }));
        markDirty();
    };

    const handleSiteUrlBlur = async (eventOrUrl) => {
        const isString = typeof eventOrUrl === "string";
        const eventType = isString ? "blur" : (eventOrUrl?.type ?? "blur");
        const eventKey = isString ? undefined : eventOrUrl?.key;
        const siteURL = (isString ? eventOrUrl : (eventOrUrl?.target?.value ?? "")).trim();

        if (
            validateSkipUrlCheckOnEvent({
                eventType,
                key: eventKey,
                isUrlUnique: preEnrollment.isUrlUnique,
            })
        ) {
            return;
        }

        if (!siteURL) {
            return;
        }

        setPreEnrollment((previous) => ({
            ...previous,
            isUrlUnique: null,
            shouldShowUrlCheck: false,
        }));

        try {
            const response = await fetchPlanManagerValidateOeUrl(siteURL);
            setPreEnrollment((previous) => ({
                ...previous,
                ...mapValidateOeUrlResponse(response ?? {}),
            }));
        } catch {
            setPreEnrollment((previous) => ({
                ...previous,
                isUrlUnique: false,
                isUrlValid: false,
                shouldShowUrlCheck: true,
            }));
        }
    };

    const handlePublishClick = () =>
        openModal(preEnrollment.publish ? "unpublish" : "publish");

    const handlePlanTypeChange = (planType) => {
        setPlanAffiliation({ planType, selectedClientId: "", plans: [] });
        markDirty();
    };

    const handleClientIdChange = (clientId) => {
        const selectedClient = clientOptions.find(
            (client) => client.id === clientId,
        );
        const plans = (selectedClient?.planIds ?? []).map((planId) => ({
            planId,
            networkAffiliation: "",
        }));
        setPlanAffiliation((previous) => ({
            ...previous,
            selectedClientId: clientId,
            plans,
        }));
        markDirty();
    };

    const handleNetworkAffiliationChange = (planId, networkValue) => {
        setPlanAffiliation((previous) => ({
            ...previous,
            plans: previous.plans.map((plan) =>
                plan.planId === planId
                    ? { ...plan, networkAffiliation: networkValue }
                    : plan,
            ),
        }));
        markDirty();
    };

    const handleBackClick = (event) => {
        if (!hasUnsavedChanges) {
            return;
        }
        event.preventDefault();
        openModal("unsavedWork");
    };

    const modalHandlers = {
        onExcludePreEnrollmentSave: commitSave,
        onCloseExcludePreEnrollment: () => closeModal("excludePreEnrollment"),
        onSaveWithoutPublishSave: commitSave,
        onSaveWithoutPublishPublish: () => {
            closeModal("saveWithoutPublish");
            openModal("publish");
        },
        onCloseSaveWithoutPublish: () => closeModal("saveWithoutPublish"),
        onClosePlanAffiliationWarning: () =>
            closeModal("planAffiliationWarning"),
        onUnsavedWorkLeave: () => {
            setUnsavedChanges(false);
            closeModal("unsavedWork");
            if (navigationBlocker.state === "blocked") {
                navigationBlocker.proceed();
                return;
            }
            navigate(NETWORKS_LIST_ROUTE);
        },
        onUnsavedWorkContinue: () => {
            closeModal("unsavedWork");
            if (navigationBlocker.state === "blocked") {
                navigationBlocker.reset();
            }
        },
        onDeleteNetworkConfirm: handleDeleteNetworkConfirm,
        onCloseDeleteNetwork: () => {
            setDeleteNetworkIndex(null);
            closeModal("deleteNetwork");
        },
        onConfirmEditUrl: () => {
            setPreEnrollment((previous) => ({
                ...previous,
                isOeUrlDisabled: false,
            }));
            closeModal("confirmEditUrl");
        },
        onCloseConfirmEditUrl: () => closeModal("confirmEditUrl"),
        onConfirmPublish: async () => {
            const name = (clientNameParam || clientName || "").trim();

            if (!name) {
                toast.error(
                    CONFIG_TOAST_MESSAGES.PUBLISH_ERROR,
                    CONFIG_TOAST_OPTIONS,
                );
                return;
            }

            try {
                const result = await fetchPlanManagerPublishOeConfig(name);

                if (result == null) {
                    toast.error(
                        CONFIG_TOAST_MESSAGES.PUBLISH_ERROR,
                        CONFIG_TOAST_OPTIONS,
                    );
                    return;
                }

                setPreEnrollment((previous) =>
                    lockSavedCustomContent({
                        ...previous,
                        ...(result.preEnrollment ?? {}),
                        publish: Boolean(result?.preEnrollment?.publish ?? true),
                    }),
                );
                await invalidateClientQueries(name);
                toast.success(
                    CONFIG_TOAST_MESSAGES.PUBLISH,
                    CONFIG_TOAST_OPTIONS,
                );
                closeModal("publish");
            } catch {
                toast.error(
                    CONFIG_TOAST_MESSAGES.PUBLISH_ERROR,
                    CONFIG_TOAST_OPTIONS,
                );
            }
        },
        onClosePublish: () => closeModal("publish"),
        onConfirmUnpublish: async () => {
            const name = (clientNameParam || clientName || "").trim();

            if (!name) {
                toast.error(
                    CONFIG_TOAST_MESSAGES.UNPUBLISH_ERROR,
                    CONFIG_TOAST_OPTIONS,
                );
                return;
            }

            try {
                const result = await fetchPlanManagerUnpublishOeConfig(name);

                if (result == null) {
                    toast.error(
                        CONFIG_TOAST_MESSAGES.UNPUBLISH_ERROR,
                        CONFIG_TOAST_OPTIONS,
                    );
                    return;
                }

                setPreEnrollment((previous) =>
                    lockSavedCustomContent({
                        ...previous,
                        ...(result.preEnrollment ?? {}),
                        publish: Boolean(result?.preEnrollment?.publish ?? false),
                    }),
                );
                await invalidateClientQueries(name);
                toast.info(
                    CONFIG_TOAST_MESSAGES.UNPUBLISH,
                    CONFIG_TOAST_OPTIONS,
                );
                closeModal("unpublish");
            } catch {
                toast.error(
                    CONFIG_TOAST_MESSAGES.UNPUBLISH_ERROR,
                    CONFIG_TOAST_OPTIONS,
                );
            }
        },
        onCloseUnpublish: () => closeModal("unpublish"),
        onConfirmRemoveCustomContent: () => {
            setPreEnrollment((previous) => ({
                ...previous,
                customContent: false,
            }));
            markDirty();
            closeModal("removeCustomContent");
        },
        onCloseRemoveCustomContent: () => closeModal("removeCustomContent"),
    };

    const configTabs = [
        {
            value: ConfigTabId.NETWORKS,
            label: "Networks",
            enabled: true,
            id: `config-tab-${ConfigTabId.NETWORKS}`,
            controls: `config-panel-${ConfigTabId.NETWORKS}`,
        },
        {
            value: ConfigTabId.PRE_ENROLLMENT,
            label: "Pre-enrollment",
            enabled: isPreEnrollmentEnabled,
            disabledTooltip: preEnrollmentDisabledTooltip,
            id: `config-tab-${ConfigTabId.PRE_ENROLLMENT}`,
            controls: `config-panel-${ConfigTabId.PRE_ENROLLMENT}`,
        },
        {
            value: ConfigTabId.PLAN_AFFILIATION,
            label: "Plan affiliation",
            enabled: isPlanAffiliationEnabled,
            disabledTooltip: planAffiliationDisabledTooltip,
            id: `config-tab-${ConfigTabId.PLAN_AFFILIATION}`,
            controls: `config-panel-${ConfigTabId.PLAN_AFFILIATION}`,
        },
    ];

    const urlValidation = {
        isUrlValid: preEnrollment.isUrlValid,
        isUrlUnique: preEnrollment.isUrlUnique,
        shouldShowUrlCheck: preEnrollment.shouldShowUrlCheck,
    };

    return {
        isEditMode: Boolean(clientNameParam),
        clientNameParam,
        clientName,
        networkConfigs,
        preEnrollment,
        planAffiliation,
        selectedTabId,
        modals,
        savedSuccess,
        isSaveEnabled,
        networkNameOptions,
        clientOptions,
        seriesOptions,
        configTabs,
        modalHandlers,
        urlValidation,
        isLoading,
        isError,
        loadErrorMessage,
        handleClientNameChange,
        handleNetworkFieldChange,
        handleDirectoryTypeChange,
        handleIncludePreEnrollmentChange,
        handleSeriesChange,
        handlePreferredChange,
        handleExternalFieldChange,
        handleDeleteClick,
        handleAddNetwork,
        handleCustomContentChange,
        handleDisplayPcpActivationBadgeChange,
        handleDisplayReferralRequiredBadgeChange,
        handleSiteUrlChange,
        handleSiteUrlBlur,
        handleUrlCheck: handleSiteUrlBlur,
        handlePublishClick,
        handlePlanTypeChange,
        handleClientIdChange,
        handleNetworkAffiliationChange,
        handleBackClick,
        handleTabChange,
        handleSave,
        openModal,
    };
}
